import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Dimensions,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { LineChart, BarChart, PieChart } from 'react-native-chart-kit';
import type { TutoringAnalytics } from '@coffeeplexity/shared/src/types/aiTutoring';

interface TutoringEffectivenessChartProps {
  analytics: TutoringAnalytics | null;
  loading: boolean;
}

const { width: screenWidth } = Dimensions.get('window');
const chartWidth = screenWidth - 80;

const TutoringEffectivenessChart: React.FC<TutoringEffectivenessChartProps> = ({
  analytics,
  loading,
}) => {
  const [selectedChart, setSelectedChart] = useState<
    'effectiveness' | 'engagement' | 'outcomes' | 'sessions'
  >('effectiveness');

  const chartTypes = [
    { key: 'effectiveness', label: 'Effectiveness', icon: 'trending-up' },
    { key: 'engagement', label: 'Engagement', icon: 'favorite' },
    { key: 'outcomes', label: 'Outcomes', icon: 'school' },
    { key: 'sessions', label: 'Sessions', icon: 'history' },
  ];

  const chartConfig = {
    backgroundColor: '#ffffff',
    backgroundGradientFrom: '#ffffff',
    backgroundGradientTo: '#ffffff',
    decimalPlaces: 0,
    color: (opacity = 1) => `rgba(0, 122, 255, ${opacity})`,
    labelColor: (opacity = 1) => `rgba(26, 26, 26, ${opacity})`,
    style: {
      borderRadius: 16,
    },
    propsForDots: {
      r: '4',
      strokeWidth: '2',
      stroke: '#007AFF',
    },
    propsForBackgroundLines: {
      strokeDasharray: '',
      stroke: '#e1e5e9',
      strokeWidth: 1,
    },
  };

  const generateMockData = () => {
    const effectiveness = analytics?.overallEffectiveness;

    return {
      effectiveness: {
        labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
        datasets: [
          {
            data: [
              (effectiveness?.effectivenessScore || 70) - 15,
              (effectiveness?.effectivenessScore || 70) - 8,
              (effectiveness?.effectivenessScore || 70) - 3,
              effectiveness?.effectivenessScore || 70,
            ],
          },
        ],
      },
      engagement: {
        labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        datasets: [
          {
            data: [
              (effectiveness?.engagementLevel || 80) - 10,
              (effectiveness?.engagementLevel || 80) - 5,
              effectiveness?.engagementLevel || 80,
              (effectiveness?.engagementLevel || 80) + 5,
              (effectiveness?.engagementLevel || 80) - 2,
              (effectiveness?.engagementLevel || 80) + 8,
              (effectiveness?.engagementLevel || 80) + 3,
            ],
          },
        ],
      },
      outcomes: [
        {
          name: 'Achieved',
          population:
            analytics?.learningOutcomeTracking?.achievedOutcomes || 15,
          color: '#4CAF50',
          legendFontColor: '#1a1a1a',
          legendFontSize: 12,
        },
        {
          name: 'Partial',
          population:
            analytics?.learningOutcomeTracking?.partiallyAchieved || 8,
          color: '#FF9800',
          legendFontColor: '#1a1a1a',
          legendFontSize: 12,
        },
        {
          name: 'Pending',
          population:
            (analytics?.learningOutcomeTracking?.totalOutcomes || 30) -
            (analytics?.learningOutcomeTracking?.achievedOutcomes || 15) -
            (analytics?.learningOutcomeTracking?.partiallyAchieved || 8),
          color: '#e1e5e9',
          legendFontColor: '#1a1a1a',
          legendFontSize: 12,
        },
      ],
      sessions: {
        labels: ['Explanation', 'Practice', 'Quiz', 'Help', 'Review'],
        datasets: [
          {
            data: [
              analytics?.sessionAnalytics?.filter(
                s => s.sessionType === 'explanation'
              ).length || 5,
              analytics?.sessionAnalytics?.filter(
                s => s.sessionType === 'practice'
              ).length || 8,
              analytics?.sessionAnalytics?.filter(s => s.sessionType === 'quiz')
                .length || 3,
              analytics?.sessionAnalytics?.filter(s => s.sessionType === 'help')
                .length || 12,
              analytics?.sessionAnalytics?.filter(
                s => s.sessionType === 'review'
              ).length || 2,
            ],
          },
        ],
      },
    };
  };

  const mockData = generateMockData();

  const renderEffectivenessMetrics = () => {
    const effectiveness = analytics?.overallEffectiveness;

    const metrics = [
      {
        title: 'Learning Velocity',
        value: effectiveness?.learningVelocity || 0,
        unit: 'concepts/hr',
        icon: 'speed',
        color: '#4CAF50',
        trend: '+12%',
      },
      {
        title: 'Retention Rate',
        value: effectiveness?.retentionRate || 0,
        unit: '%',
        icon: 'memory',
        color: '#2196F3',
        trend: '+8%',
      },
      {
        title: 'Completion Rate',
        value: effectiveness?.completionRate || 0,
        unit: '%',
        icon: 'check-circle',
        color: '#FF9800',
        trend: '+5%',
      },
      {
        title: 'User Satisfaction',
        value: effectiveness?.userSatisfaction || 0,
        unit: '%',
        icon: 'sentiment-satisfied',
        color: '#E91E63',
        trend: '+15%',
      },
    ];

    return (
      <View style={styles.metricsGrid}>
        {metrics.map((metric, index) => (
          <View
            key={index}
            style={[styles.metricCard, { borderLeftColor: metric.color }]}
          >
            <View style={styles.metricHeader}>
              <Icon name={metric.icon} size={20} color={metric.color} />
              <Text style={styles.metricTitle}>{metric.title}</Text>
            </View>
            <Text style={styles.metricValue}>
              {loading ? '...' : `${Math.round(metric.value)}${metric.unit}`}
            </Text>
            <Text style={[styles.metricTrend, { color: metric.color }]}>
              {metric.trend} this week
            </Text>
          </View>
        ))}
      </View>
    );
  };

  const renderChart = () => {
    if (loading) {
      return (
        <View style={styles.loadingChart}>
          <Icon name='analytics' size={48} color='#ccc' />
          <Text style={styles.loadingText}>Loading analytics...</Text>
        </View>
      );
    }

    switch (selectedChart) {
      case 'effectiveness':
        return (
          <View style={styles.chartContainer}>
            <Text style={styles.chartTitle}>Effectiveness Over Time</Text>
            <LineChart
              data={mockData.effectiveness}
              width={chartWidth}
              height={220}
              chartConfig={chartConfig}
              bezier
              style={styles.chart}
            />
          </View>
        );

      case 'engagement':
        return (
          <View style={styles.chartContainer}>
            <Text style={styles.chartTitle}>Weekly Engagement Pattern</Text>
            <LineChart
              data={mockData.engagement}
              width={chartWidth}
              height={220}
              chartConfig={chartConfig}
              style={styles.chart}
            />
          </View>
        );

      case 'outcomes':
        return (
          <View style={styles.chartContainer}>
            <Text style={styles.chartTitle}>
              Learning Outcomes Distribution
            </Text>
            <PieChart
              data={mockData.outcomes}
              width={chartWidth}
              height={220}
              chartConfig={chartConfig}
              accessor='population'
              backgroundColor='transparent'
              paddingLeft='15'
              style={styles.chart}
            />
          </View>
        );

      case 'sessions':
        return (
          <View style={styles.chartContainer}>
            <Text style={styles.chartTitle}>Session Types Distribution</Text>
            <BarChart
              data={mockData.sessions}
              width={chartWidth}
              height={220}
              chartConfig={chartConfig}
              style={styles.chart}
              showValuesOnTopOfBars
            />
          </View>
        );

      default:
        return null;
    }
  };

  const renderInsights = () => {
    const insights = [
      {
        icon: 'trending-up',
        color: '#4CAF50',
        title: 'Improving Performance',
        description:
          'Your learning velocity has increased by 12% this week, showing consistent improvement.',
      },
      {
        icon: 'psychology',
        color: '#2196F3',
        title: 'Optimal Learning Time',
        description:
          'You perform best during morning sessions (9-11 AM) with 85% effectiveness.',
      },
      {
        icon: 'lightbulb',
        color: '#FF9800',
        title: 'Adaptation Success',
        description:
          'AI adaptations have improved your comprehension by 23% on average.',
      },
    ];

    return (
      <View style={styles.insightsSection}>
        <Text style={styles.insightsTitle}>AI Insights</Text>
        {insights.map((insight, index) => (
          <View key={index} style={styles.insightCard}>
            <View
              style={[
                styles.insightIcon,
                { backgroundColor: `${insight.color}15` },
              ]}
            >
              <Icon name={insight.icon} size={20} color={insight.color} />
            </View>
            <View style={styles.insightContent}>
              <Text style={styles.insightTitle}>{insight.title}</Text>
              <Text style={styles.insightDescription}>
                {insight.description}
              </Text>
            </View>
          </View>
        ))}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Tutoring Analytics</Text>
        <Text style={styles.subtitle}>
          Track your AI-powered learning progress
        </Text>
      </View>

      {/* Effectiveness Metrics */}
      {renderEffectivenessMetrics()}

      {/* Chart Type Selector */}
      <View style={styles.chartSelector}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.chartSelectorContent}
        >
          {chartTypes.map(type => (
            <TouchableOpacity
              key={type.key}
              style={[
                styles.chartTypeButton,
                selectedChart === type.key && styles.activeChartTypeButton,
              ]}
              onPress={() => setSelectedChart(type.key as any)}
            >
              <Icon
                name={type.icon}
                size={18}
                color={selectedChart === type.key ? '#007AFF' : '#666'}
              />
              <Text
                style={[
                  styles.chartTypeText,
                  selectedChart === type.key && styles.activeChartTypeText,
                ]}
              >
                {type.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Chart Display */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.chartScrollContent}
      >
        {renderChart()}
      </ScrollView>

      {/* AI Insights */}
      {renderInsights()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1a1a1a',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: '#666',
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 24,
  },
  metricCard: {
    flex: 1,
    minWidth: '45%',
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
    borderLeftWidth: 3,
  },
  metricHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  metricTitle: {
    fontSize: 12,
    fontWeight: '500',
    color: '#666',
    marginLeft: 6,
  },
  metricValue: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1a1a1a',
    marginBottom: 4,
  },
  metricTrend: {
    fontSize: 10,
    fontWeight: '500',
  },
  chartSelector: {
    marginBottom: 16,
  },
  chartSelectorContent: {
    paddingRight: 20,
  },
  chartTypeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 16,
    backgroundColor: '#f8f9fa',
    marginRight: 8,
    gap: 6,
  },
  activeChartTypeButton: {
    backgroundColor: '#e3f2fd',
  },
  chartTypeText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#666',
  },
  activeChartTypeText: {
    color: '#007AFF',
  },
  chartScrollContent: {
    paddingRight: 20,
  },
  chartContainer: {
    alignItems: 'center',
    marginBottom: 24,
  },
  chartTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: 12,
  },
  chart: {
    borderRadius: 16,
  },
  loadingChart: {
    alignItems: 'center',
    paddingVertical: 60,
  },
  loadingText: {
    fontSize: 16,
    color: '#666',
    marginTop: 12,
  },
  insightsSection: {
    borderTopWidth: 1,
    borderTopColor: '#e1e5e9',
    paddingTop: 20,
  },
  insightsTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: 16,
  },
  insightCard: {
    flexDirection: 'row',
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  insightIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  insightContent: {
    flex: 1,
  },
  insightTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: 4,
  },
  insightDescription: {
    fontSize: 12,
    color: '#666',
    lineHeight: 16,
  },
});

export default TutoringEffectivenessChart;
